import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { 
  Bell, BellOff, Clock, AlarmClock, 
  Moon, Sun, Pill, Dumbbell, Brain, X
} from 'lucide-react';
import { useLocalNotifications } from '@/hooks/useLocalNotifications';
import { toast } from 'sonner';
import { TypewriterMessage } from '@/components/onboarding/TypewriterMessage';

interface Reminder {
  id: string;
  title: string;
  description: string;
  scheduledTime: string; // HH:MM format
  enabled: boolean;
  icon: 'morning_checkin' | 'evening_checkin' | 'medication' | 'meditation' | 'exercise' | 'custom';
  snoozedUntil?: Date;
}

const SNOOZE_OPTIONS = [
  { value: 5, label: '5 min' },
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 60, label: '1 hour' },
  { value: 120, label: '2 hours' },
];

const ICON_MAP = {
  morning_checkin: Sun,
  evening_checkin: Moon,
  medication: Pill,
  meditation: Brain,
  exercise: Dumbbell,
  custom: Bell
};

interface ReminderSnoozeProps {
  onClose?: () => void;
  compact?: boolean;
}

export default function ReminderSnooze({ onClose, compact = false }: ReminderSnoozeProps) {
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [selectedReminder, setSelectedReminder] = useState<string | null>(null);
  const [snoozeMinutes, setSnoozeMinutes] = useState(15);
  const { scheduleDailyCheckIn, scheduleMedicationReminder, cancelNotification, getPendingNotifications } = useLocalNotifications();

  useEffect(() => {
    loadReminders();
  }, []);

  const loadReminders = () => {
    // Load from localStorage
    const stored = localStorage.getItem('therappi_reminders');
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as Reminder[];
        // Filter out expired snoozes
        const updated = parsed.map(r => ({
          ...r,
          snoozedUntil: r.snoozedUntil ? new Date(r.snoozedUntil) : undefined
        })).map(r => {
          if (r.snoozedUntil && new Date() > r.snoozedUntil) {
            return { ...r, snoozedUntil: undefined };
          }
          return r;
        });
        setReminders(updated);
      } catch (e) {
        setDefaultReminders();
      }
    } else {
      setDefaultReminders();
    }
  };

  const setDefaultReminders = () => {
    const defaults: Reminder[] = [
      {
        id: 'morning_checkin',
        title: 'Morning Check-In',
        description: 'Start your day with a wellness check',
        scheduledTime: '08:00',
        enabled: true,
        icon: 'morning_checkin'
      },
      {
        id: 'evening_checkin',
        title: 'Evening Self-Care',
        description: 'Reflect on your day and log tools used',
        scheduledTime: '20:00',
        enabled: true,
        icon: 'evening_checkin'
      },
      {
        id: 'medication_1',
        title: 'Take Medication',
        description: 'Daily medication reminder',
        scheduledTime: '09:00',
        enabled: false,
        icon: 'medication'
      },
      {
        id: 'meditation',
        title: 'Meditation Time',
        description: 'Daily mindfulness practice',
        scheduledTime: '07:00',
        enabled: false,
        icon: 'meditation'
      }
    ];
    setReminders(defaults);
    saveReminders(defaults);
  };

  const saveReminders = (updated: Reminder[]) => {
    localStorage.setItem('therappi_reminders', JSON.stringify(updated));
  };

  const handleToggleReminder = async (reminderId: string) => {
    const updated = reminders.map(r => {
      if (r.id === reminderId) {
        const newEnabled = !r.enabled;
        
        // Schedule or cancel notification
        if (newEnabled) {
          const [hour, minute] = r.scheduledTime.split(':').map(Number);
          if (r.icon === 'morning_checkin' || r.icon === 'evening_checkin') {
            scheduleDailyCheckIn(hour, minute);
          } else if (r.icon === 'medication') {
            scheduleMedicationReminder(r.id, r.title, hour, minute);
          }
          toast.success(`${r.title} reminder enabled`);
        } else {
          // Cancel notification (would need notification ID tracking)
          toast.success(`${r.title} reminder disabled`);
        }
        
        return { ...r, enabled: newEnabled, snoozedUntil: undefined };
      }
      return r;
    });
    
    setReminders(updated);
    saveReminders(updated);
  };

  const handleSnooze = (reminderId: string) => {
    const snoozeUntil = new Date();
    snoozeUntil.setMinutes(snoozeUntil.getMinutes() + snoozeMinutes);
    
    const updated = reminders.map(r => {
      if (r.id === reminderId) {
        return { ...r, snoozedUntil: snoozeUntil };
      }
      return r;
    });
    
    setReminders(updated);
    saveReminders(updated);
    setSelectedReminder(null);
    
    toast.success(`Snoozed for ${snoozeMinutes} minutes`, {
      description: `Will remind you at ${snoozeUntil.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    });
  };

  const handleTimeChange = (reminderId: string, time: string) => {
    const updated = reminders.map(r => {
      if (r.id === reminderId) {
        return { ...r, scheduledTime: time };
      }
      return r;
    });
    
    setReminders(updated);
    saveReminders(updated);
  };

  const isSnoozed = (reminder: Reminder) => {
    return reminder.snoozedUntil && new Date() < reminder.snoozedUntil;
  };

  const getSnoozedTimeLeft = (reminder: Reminder) => {
    if (!reminder.snoozedUntil) return '';
    const diff = Math.round((reminder.snoozedUntil.getTime() - Date.now()) / 60000);
    return diff > 0 ? `${diff} min left` : '';
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        {reminders.filter(r => r.enabled).map(reminder => {
          const IconComponent = ICON_MAP[reminder.icon];
          const snoozed = isSnoozed(reminder);
          
          return (
            <Badge 
              key={reminder.id}
              variant={snoozed ? 'secondary' : 'outline'}
              className={`gap-1 cursor-pointer transition-all hover:scale-105 ${snoozed ? 'opacity-60' : ''}`}
              onClick={() => setSelectedReminder(reminder.id)}
            >
              <IconComponent className="w-3 h-3" />
              <span>{reminder.scheduledTime}</span>
              {snoozed && <BellOff className="w-3 h-3" />}
            </Badge>
          );
        })}
      </div>
    );
  }

  return (
    <Card className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <AlarmClock className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold">Reminder Settings</h3>
            <p className="text-sm text-muted-foreground">Manage your wellness reminders</p>
          </div>
        </div>
        {onClose && (
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Typewriter Guidance */}
      <div className="p-3 bg-primary/10 rounded-lg border border-primary/20">
        <p className="text-sm text-foreground">
          <TypewriterMessage 
            text="Gentle nudges at the right time can make all the difference. Setting reminders isn't about pressure - it's about creating moments of pause in your day. You can always snooze when life gets busy."
            speed={18}
            showCursor={false}
          />
        </p>
      </div>

      <div className="space-y-3">
        {reminders.map(reminder => {
          const IconComponent = ICON_MAP[reminder.icon];
          const snoozed = isSnoozed(reminder);
          const isSelected = selectedReminder === reminder.id;
          
          return (
            <div 
              key={reminder.id}
              className={`p-4 rounded-lg border transition-all ${
                snoozed 
                  ? 'bg-muted/50 border-border/50 opacity-70' 
                  : reminder.enabled 
                    ? 'bg-primary/5 border-primary/20' 
                    : 'bg-muted/30 border-border'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${
                  reminder.enabled && !snoozed ? 'bg-primary/10' : 'bg-muted'
                }`}>
                  <IconComponent className={`w-5 h-5 ${
                    reminder.enabled && !snoozed ? 'text-primary' : 'text-muted-foreground'
                  }`} />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{reminder.title}</p>
                    {snoozed && (
                      <Badge variant="secondary" className="text-xs gap-1">
                        <BellOff className="w-3 h-3" />
                        {getSnoozedTimeLeft(reminder)}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{reminder.description}</p>
                </div>
                
                <div className="flex items-center gap-2">
                  <input
                    type="time"
                    value={reminder.scheduledTime}
                    onChange={(e) => handleTimeChange(reminder.id, e.target.value)}
                    className="text-sm bg-transparent border-none focus:outline-none text-right w-16"
                    disabled={!reminder.enabled}
                  />
                  
                  <Switch
                    checked={reminder.enabled}
                    onCheckedChange={() => handleToggleReminder(reminder.id)}
                  />
                </div>
              </div>
              
              {/* Snooze Controls */}
              {reminder.enabled && !snoozed && (
                <div className="mt-3 pt-3 border-t border-border/50">
                  {isSelected ? (
                    <div className="space-y-3 animate-fade-in">
                      <div className="flex items-center gap-2 flex-wrap">
                        {SNOOZE_OPTIONS.map(opt => (
                          <Button
                            key={opt.value}
                            variant={snoozeMinutes === opt.value ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setSnoozeMinutes(opt.value)}
                            className="text-xs"
                          >
                            {opt.label}
                          </Button>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          onClick={() => handleSnooze(reminder.id)}
                          className="gap-1"
                        >
                          <BellOff className="w-3 h-3" />
                          Snooze for {snoozeMinutes} min
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => setSelectedReminder(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedReminder(reminder.id)}
                      className="text-xs text-muted-foreground gap-1"
                    >
                      <Clock className="w-3 h-3" />
                      Snooze this reminder
                    </Button>
                  )}
                </div>
              )}
              
              {/* Snoozed state - option to cancel snooze */}
              {snoozed && (
                <div className="mt-3 pt-3 border-t border-border/50">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const updated = reminders.map(r => 
                        r.id === reminder.id ? { ...r, snoozedUntil: undefined } : r
                      );
                      setReminders(updated);
                      saveReminders(updated);
                      toast.success('Snooze cancelled');
                    }}
                    className="text-xs gap-1"
                  >
                    <Bell className="w-3 h-3" />
                    Cancel snooze
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="pt-4 border-t border-border">
        <p className="text-xs text-muted-foreground text-center">
          Reminders require notification permissions on your device
        </p>
      </div>
    </Card>
  );
}
